import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    twoFactorCode: '',
    loginMethod: 'email'
  });
  
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const qrRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (formData.loginMethod === 'discord') {
        window.location.href = 'https://examguard.net/api/auth/discord';
        return;
      }
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.twoFactorRequired) {
          setShowTwoFactor(true);
          if (data.qrCode) {
            setTimeout(() => {
              qrRef.current.innerHTML = `<img src="${data.qrCode}" alt="QR Code for 2FA" />`;
            }, 100);
          }
        } else {
          // Usar el contexto para login
          login(data.token, data.user);
          navigate('/dashboard');
        }
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFactor = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          twoFactorCode: formData.twoFactorCode
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        login(data.token, data.user);
        navigate('/dashboard');
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-card-bg p-8 rounded-xl shadow-2xl"
      >
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Sign in to ExamGuard
          </h2>
          <p className="mt-2 text-center text-sm text-text-secondary">
            Or{' '}
            <Link to="/register" className="font-medium text-primary hover:text-accent">
              create a new account
            </Link>
          </p>
        </div>
        
        <AnimatePresence mode="wait">
          {!showTwoFactor ? (
            <motion.form
              key="loginForm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-8 space-y-6"
              onSubmit={handleSubmit}
            >
              <div className="flex space-x-4 mb-6">
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    formData.loginMethod === 'email'
                      ? 'bg-primary text-white'
                      : 'bg-transparent border border-border-color text-text-secondary'
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, loginMethod: 'email' }))}
                >
                  Email
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center ${
                    formData.loginMethod === 'discord'
                      ? 'bg-primary text-white'
                      : 'bg-transparent border border-border-color text-text-secondary'
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, loginMethod: 'discord' }))}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.042.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  Discord
                </button>
              </div>
              
              {formData.loginMethod === 'email' && (
                <>
                  <div>
                    <label htmlFor="email" className="sr-only">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-border-color bg-dark-bg/50 placeholder-text-secondary text-white focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="sr-only">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-border-color bg-dark-bg/50 placeholder-text-secondary text-white focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}
              
              {error && (
                <div className="text-red-500 text-sm">{error}</div>
              )}
              
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.div
              key="twoFactorForm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-8 space-y-6"
            >
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Two-Factor Authentication</h3>
                <p className="text-text-secondary mb-6">
                  Scan the QR code with your authenticator app or enter the code manually
                </p>
                
                <div 
                  ref={qrRef} 
                  className="flex justify-center mb-6 p-4 bg-white rounded-lg"
                ></div>
                
                <div>
                  <label htmlFor="twoFactorCode" className="sr-only">
                    Verification code
                  </label>
                  <input
                    id="twoFactorCode"
                    name="twoFactorCode"
                    type="text"
                    required
                    className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-border-color bg-dark-bg/50 placeholder-text-secondary text-white focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm mb-4"
                    placeholder="6-digit code"
                    value={formData.twoFactorCode}
                    onChange={handleChange}
                  />
                </div>
                
                {error && (
                  <div className="text-red-500 text-sm mb-4">{error}</div>
                )}
                
                <button
                  onClick={verifyTwoFactor}
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Login;